import { useEffect } from "react";

// Compute the site URL dynamically at runtime.
// - In the browser, use the current origin (works for preview, prod, custom domain)
// - Fallback to REACT_APP_SITE_URL if set, else empty string during SSR
export const SITE_URL =
  (typeof window !== "undefined" && window.location && window.location.origin) ||
  process.env.REACT_APP_SITE_URL ||
  "";

const DEFAULT_OG_IMAGE =
  "https://customer-assets.emergentagent.com/job_penow-renovation/artifacts/alkkqvg7_ChatGPT%20Image%209%20juil.%202026%2C%2006_48_48.png";

function upsertMeta({ name, property, content }) {
  if (!content) return;
  const selector = name
    ? `meta[name="${name}"]`
    : `meta[property="${property}"]`;
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    if (name) el.setAttribute("name", name);
    if (property) el.setAttribute("property", property);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertLink(rel, href) {
  if (!href) return;
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

const MANAGED_JSONLD_ID = "penow-page-jsonld";

function upsertJsonLd(json) {
  // remove previous page-specific JSON-LD
  const prev = document.getElementById(MANAGED_JSONLD_ID);
  if (prev) prev.remove();
  if (!json) return;
  const script = document.createElement("script");
  script.type = "application/ld+json";
  script.id = MANAGED_JSONLD_ID;
  script.text = JSON.stringify(json);
  document.head.appendChild(script);
}

/**
 * Set page-level SEO metadata.
 *
 * @param {Object} opts
 * @param {string} opts.title       - Page title (will be suffixed with " | PENOW")
 * @param {string} opts.description - Meta description
 * @param {string} [opts.keywords]  - Comma-separated keywords
 * @param {string} [opts.path]      - Path (e.g. "/services") — used for canonical & og:url
 * @param {string} [opts.ogImage]   - Custom OG image (defaults to hero image)
 * @param {Object} [opts.jsonLd]    - Page-scoped JSON-LD schema (Service, Breadcrumb, etc.)
 */
export default function useSEO({
  title,
  description,
  keywords,
  path = "",
  ogImage,
  jsonLd,
}) {
  useEffect(() => {
    const fullTitle = title ? `${title} | PENOW` : "PENOW — Rénovation, climatisation & PAC à Fondettes (37)";
    const url = `${SITE_URL}${path}`;
    const image = ogImage || DEFAULT_OG_IMAGE;

    document.title = fullTitle;

    upsertMeta({ name: "description", content: description });
    if (keywords) upsertMeta({ name: "keywords", content: keywords });

    // Open Graph
    upsertMeta({ property: "og:title", content: fullTitle });
    upsertMeta({ property: "og:description", content: description });
    upsertMeta({ property: "og:url", content: url });
    upsertMeta({ property: "og:image", content: image });

    // Twitter
    upsertMeta({ name: "twitter:title", content: fullTitle });
    upsertMeta({ name: "twitter:description", content: description });
    upsertMeta({ name: "twitter:image", content: image });

    // Canonical
    upsertLink("canonical", url);

    // JSON-LD (page-specific)
    upsertJsonLd(jsonLd);
  }, [title, description, keywords, path, ogImage, jsonLd]);
}
